import type { MetaTagsProps } from 'svelte-meta-tags';

export const SITE_URL = 'https://edvello.com';
export const SITE_NAME = 'Edvello';
export const APP_URL = 'https://app.edvello.com';
export const SIGN_IN_URL = `${APP_URL}/login`;
export const SUPPORT_EMAIL = 'support@edvello.com';
export const CONTACT_PATH = '/contact';
export const CONTACT_ANCHOR = 'contact';
export const SITE_DESCRIPTION =
  'Edvello is coaching institute management software for India. Organise courses and batches, record attendance and review student work in one learning platform.';
export const SOCIAL_IMAGE_PATH = '/og/edvello.jpg';
export const SOCIAL_IMAGE_ALT = 'Edvello logo';
export const SOCIAL_IMAGE_WIDTH = 1200;
export const SOCIAL_IMAGE_HEIGHT = 630;
/** Public profile URLs included as schema.org `sameAs` links on the organization. */
export const SOCIAL_PROFILE_URLS: string[] = [];

export const SITE_KEYWORDS = [
  'Edvello',
  'coaching institute management software',
  'tuition management software',
  'coaching management software India',
  'learning management system for coaching institutes',
  'LMS India',
  'online live classes platform',
  'batch management software',
  'student attendance app',
  'tuition classes app',
  'school learning platform'
];

export type SitePage = {
  path: string;
  title: string;
  description: string;
  keywords: string[];
  priority: number;
  changefreq: 'daily' | 'weekly' | 'monthly' | 'yearly';
};

export const SITE_PAGES = {
  home: {
    path: '/',
    title: `Edvello — Coaching Institute Management Software India`,
    description: SITE_DESCRIPTION,
    keywords: SITE_KEYWORDS,
    priority: 1,
    changefreq: 'weekly'
  },
  product: {
    path: '/product',
    title: `LMS for Coaching Institutes & Tuition Centres | ${SITE_NAME}`,
    description:
      'Explore Edvello’s LMS for coaching institutes. Build courses, organise batches and review student work. Give your institute a branded learning website.',
    keywords: [
      'learning management system',
      'LMS for coaching institutes',
      'live online classes software',
      'online course platform',
      'attendance management',
      'online assessment software'
    ],
    priority: 0.9,
    changefreq: 'monthly'
  },
  institutions: {
    path: '/institutions',
    title: `Tuition Centre & Coaching Management Software | ${SITE_NAME}`,
    description:
      'Software for tuition centres and coaching institutes in India. Manage teachers and student batches, share course materials and record attendance with Edvello.',
    keywords: [
      'coaching centre software',
      'school management software',
      'tuition centre software',
      'software for tutors',
      'parent and student app'
    ],
    priority: 0.9,
    changefreq: 'monthly'
  },
  pricing: {
    path: '/pricing',
    title: `Pricing for Coaching & Tuition Software | ${SITE_NAME}`,
    description:
      'Edvello pricing is tailored to your coaching institute or school. Share your batches and student numbers and get a proposal for your learning platform.',
    keywords: ['coaching software pricing', 'tuition software price', 'LMS pricing India'],
    priority: 0.8,
    changefreq: 'monthly'
  },
  contact: {
    path: '/contact',
    title: `Book a Demo of Coaching Institute Software | ${SITE_NAME}`,
    description:
      'Book an Edvello demo. Tell us about your coaching institute, tuition classes or school and we will help you set up live classes, batches and courses.',
    keywords: ['coaching software demo', 'tuition management software demo', 'contact Edvello'],
    priority: 0.8,
    changefreq: 'yearly'
  },
  privacy: {
    path: '/privacy',
    title: `Privacy policy | ${SITE_NAME}`,
    description:
      'How Edvello collects, uses, stores and protects personal data when you use our websites and services.',
    keywords: [],
    priority: 0.3,
    changefreq: 'yearly'
  },
  terms: {
    path: '/tos',
    title: `Terms of service | ${SITE_NAME}`,
    description: 'The terms that apply when you use Edvello websites and services.',
    keywords: [],
    priority: 0.3,
    changefreq: 'yearly'
  }
} satisfies Record<string, SitePage>;

export type SitePageKey = keyof typeof SITE_PAGES;

export const NAV_LINKS = [
  { label: 'Platform', href: SITE_PAGES.product.path },
  { label: 'For institutions', href: SITE_PAGES.institutions.path },
  { label: 'Pricing', href: SITE_PAGES.pricing.path },
  { label: 'FAQ', href: '/#faq' },
  { label: 'Contact', href: CONTACT_PATH }
];

/**
 * Resolves a site path against the production origin so prerendered output never embeds the build host.
 */
export function absoluteUrl(path: string) {
  return new URL(path, SITE_URL).href;
}

/**
 * Builds the page-level meta tags merged over the layout defaults.
 */
export function pageMetaTags(key: SitePageKey): MetaTagsProps {
  const page: SitePage = SITE_PAGES[key];
  const imageUrl = absoluteUrl(SOCIAL_IMAGE_PATH);
  const socialImage = {
    url: imageUrl,
    secureUrl: imageUrl,
    alt: SOCIAL_IMAGE_ALT,
    width: SOCIAL_IMAGE_WIDTH,
    height: SOCIAL_IMAGE_HEIGHT,
    type: 'image/jpeg'
  };

  return {
    title: page.title,
    description: page.description,
    keywords: page.keywords,
    openGraph: { title: page.title, description: page.description, images: [socialImage] },
    twitter: { title: page.title, description: page.description, image: imageUrl, imageAlt: SOCIAL_IMAGE_ALT }
  };
}

/**
 * Builds meta tags for pages kept for old links but excluded from search results.
 */
export function unindexedMetaTags(title: string, description: string): MetaTagsProps {
  const fullTitle = `${title} | ${SITE_NAME}`;

  return {
    title: fullTitle,
    description,
    robots: 'noindex,follow',
    openGraph: { title: fullTitle, description },
    twitter: { title: fullTitle, description }
  };
}
